import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { folderCreateInput } from '@xdocs/schemas';
import type { FolderCreateInput } from '@xdocs/schemas';
import { Button, Input, Modal, cn, useI18n } from '@xdocs/ui';
import { applyWsAlert } from '../../lib/applyWsAlert.js';
import { safeFieldError } from '../../i18n/safeT.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { useExplorerT } from './useExplorerT.js';
import styles from './RenameDialog.module.css';

export interface NewFolderDialogProps {
  open: boolean;
  onClose: () => void;
  parentFolderId: string | null;
  onCreate: (name: string, parentFolderId: string | null) => Promise<unknown>;
}

/** `folderCreateInput.parentFolderId` carries `.default(null)`, so zod's
 * INPUT type (pre-default, what `useForm`'s field-values generic must
 * match) has it optional, while the OUTPUT type (`FolderCreateInput`,
 * post-default) requires it — RHF v7's 3-generic `useForm<TFieldValues,
 * TContext, TTransformedValues>` is exactly this split: form state uses the
 * input shape below, `handleSubmit(onSubmit)` delivers the resolver's
 * transformed (output, `FolderCreateInput`) shape. Spelled out structurally
 * rather than `z.input<typeof folderCreateInput>` because `zod` isn't a
 * direct `apps/web` dependency (only `@xdocs/schemas` is, per the
 * monorepo's import-boundary rule, E5).
 */
type FolderFormValues = Omit<FolderCreateInput, 'parentFolderId'> & { parentFolderId?: string | null | undefined };

/** F7 "New folder" — RHF + `zodResolver(folderCreateInput)` (E6, the same
 * schema the server validates against). */
export function NewFolderDialog({ open, onClose, parentFolderId, onCreate }: NewFolderDialogProps): ReactElement {
  const { t: tCommon, locale } = useI18n();
  const { t } = useExplorerT();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FolderFormValues, unknown, FolderCreateInput>({
    resolver: zodResolver(folderCreateInput),
    defaultValues: { name: '', parentFolderId },
  });

  async function onSubmit(values: FolderCreateInput): Promise<void> {
    try {
      await onCreate(values.name, parentFolderId);
      reset({ name: '', parentFolderId });
      onClose();
    } catch (err) {
      applyWsAlert(err, setError, locale);
    }
  }

  function handleClose(): void {
    reset({ name: '', parentFolderId });
    onClose();
  }

  return (
    <Modal open={open} title={t('explorer:newFolder.title')} onClose={handleClose} size="sm">
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <Input
          label={t('explorer:newFolder.label')}
          required
          {...optionalProp('error', safeFieldError(locale, errors.name?.message))}
          {...register('name')}
        />
        <div className={cn(styles['actions'])}>
          <Button variant="secondary" type="button" onClick={handleClose} disabled={isSubmitting}>
            {tCommon('common:action.cancel')}
          </Button>
          <Button variant="primary" type="submit" state={isSubmitting ? 'loading' : 'idle'}>
            {t('explorer:action.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
